import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Clock, CheckCircle2, Inbox, Star, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateShort } from "@/lib/format";

const KPI = ({ icon: Icon, label, value, highlight, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="admin-dashboard-kpi-card"
    className={`w-full rounded-2xl border bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(20,19,22,0.08)] ${highlight ? "border-[color:var(--brand-marsala)] ring-1 ring-[color:var(--brand-marsala)]/30" : "border-[color:var(--brand-border)]"}`}
  >
    <div className="flex items-center justify-between">
      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${highlight ? "bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "bg-[color:var(--brand-paper)] text-[color:var(--brand-muted)]"}`}>
        <Icon className="h-5 w-5" />
      </span>
      {highlight && value > 0 && (
        <span className="rounded-full bg-[color:var(--brand-marsala)] px-2 py-0.5 text-[10px] font-semibold text-white">Novo</span>
      )}
    </div>
    <p className="mt-4 font-serif text-3xl font-semibold text-[color:var(--brand-graphite)]">{value}</p>
    <p className="text-sm text-[color:var(--brand-muted)]">{label}</p>
  </button>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-marsala)]" /></div>;

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold text-[color:var(--brand-graphite)]">Painel LC Produção</h1>
      <p className="mt-1 text-sm text-[color:var(--brand-muted)]">Uma visão rápida dos repertórios e eventos.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI icon={CalendarClock} label="Próximos eventos" value={data.upcoming} onClick={() => navigate("/admin/agenda")} />
        <KPI icon={Clock} label="Repertórios pendentes" value={data.pending} onClick={() => navigate("/admin/clientes?status=em_preenchimento")} />
        <KPI icon={CheckCircle2} label="Repertórios concluídos" value={data.completed} onClick={() => navigate("/admin/clientes?status=aprovado")} />
        <KPI icon={Inbox} label="Novos recebidos" value={data.new_received} highlight onClick={() => navigate("/admin/clientes?status=enviado")} />
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-xl font-semibold text-[color:var(--brand-graphite)]">Novos repertórios recebidos</h2>
        <div className="mt-3 space-y-2">
          {data.notifications.filter((n) => n.type === "new_repertoire").length === 0 ? (
            <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-6 text-center text-sm text-[color:var(--brand-muted)]">
              Nenhum repertório novo por enquanto.
            </div>
          ) : (
            data.notifications
              .filter((n) => n.type === "new_repertoire")
              .map((n) => (
                <button
                  key={n.id}
                  onClick={() => navigate(`/admin/clientes/${n.event_id}`)}
                  className="flex w-full items-center justify-between rounded-2xl border border-[color:var(--brand-marsala)]/40 bg-white p-4 text-left transition-colors hover:bg-[color:var(--brand-marsala-soft)]"
                >
                  <div className="flex items-center gap-3">
                    <Star className="h-5 w-5 text-[color:var(--brand-marsala)]" />
                    <div>
                      <p className="text-sm font-semibold text-[color:var(--brand-graphite)]">{n.couple_name}</p>
                      <p className="text-xs text-[color:var(--brand-muted)]">{n.local} · {formatDateShort(n.date)}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[color:var(--brand-muted)]" />
                </button>
              ))
          )}
        </div>
      </div>
    </div>
  );
}
